import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, limit, getDocs } from 'firebase/firestore';
import { Search, X, ArrowRight } from 'lucide-react';

const UniversalSearchModal = ({ isOpen, onClose, navigate }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [postResults, setPostResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all'); // 'all' | 'users' | 'posts'

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setUserResults([]);
            setPostResults([]);
            return;
        }
    }, [isOpen]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setUserResults([]);
            setPostResults([]);
            return;
        }

        const delaySearch = setTimeout(async () => {
            setLoading(true);
            const term = searchTerm.toLowerCase().trim();

            try {
                // Fetch users
                const usersSnap = await getDocs(query(collection(db, 'users'), limit(50)));
                const matchedUsers = [];
                usersSnap.forEach(docSnap => {
                    const data = docSnap.data();
                    const name = (data.displayName || '').toLowerCase();
                    const uname = (data.username || '').toLowerCase();
                    if (name.includes(term) || uname.includes(term)) {
                        matchedUsers.push({ id: docSnap.id, ...data });
                    }
                });
                setUserResults(matchedUsers);

                // Fetch posts
                const postsSnap = await getDocs(query(collection(db, 'posts'), limit(50)));
                const matchedPosts = [];
                postsSnap.forEach(docSnap => {
                    const data = docSnap.data();
                    const text = (data.text || '').toLowerCase();
                    const mood = (data.mood || '').toLowerCase();
                    if (text.includes(term) || mood.includes(term)) {
                        matchedPosts.push({ id: docSnap.id, ...data });
                    }
                });
                setPostResults(matchedPosts);
            } catch (err) {
                console.error("Search error:", err);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(delaySearch);
    }, [searchTerm]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 pt-16 sm:pt-24 animate-fade-in">
            <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl max-w-xl w-full p-5 sm:p-6 shadow-2xl animate-scale-in flex flex-col max-h-[80vh]">
                {/* Search Bar Input */}
                <div className="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-800">
                    <Search className="w-5 h-5 text-brand-500 flex-shrink-0" />
                    <input
                        type="text"
                        autoFocus
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search people, posts, @usernames..."
                        className="w-full bg-transparent text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 text-base focus:outline-none"
                    />
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 py-3">
                    {['all', 'users', 'posts'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all ${
                                activeTab === tab
                                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Results Container */}
                <div className="overflow-y-auto space-y-4 pt-2 flex-grow pr-1 no-scrollbar">
                    {loading && (
                        <div className="py-12 text-center text-xs text-slate-400 dark:text-slate-500">
                            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                            Searching Batcheet...
                        </div>
                    )}

                    {!loading && searchTerm && userResults.length === 0 && postResults.length === 0 && (
                        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">
                            No results found for "{searchTerm}".
                        </div>
                    )}

                    {/* Users Section */}
                    {(activeTab === 'all' || activeTab === 'users') && userResults.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1">People</h4>
                            {userResults.map(user => (
                                <div
                                    key={user.id}
                                    onClick={() => {
                                        navigate('profile', { userId: user.id });
                                        onClose();
                                    }}
                                    className="p-3 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 flex items-center justify-between cursor-pointer group transition-all"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-full p-[1.5px] bg-gradient-to-tr from-brand-500 to-indigo-500 flex-shrink-0">
                                            <img
                                                src={user.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${user.id}`}
                                                alt=""
                                                className="w-full h-full rounded-full object-cover bg-white dark:bg-slate-900"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-sm text-slate-900 dark:text-slate-100 group-hover:text-brand-500 transition-colors truncate">
                                                {user.displayName}
                                            </p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">@{user.username || 'user'}</p>
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Posts Section */}
                    {(activeTab === 'all' || activeTab === 'posts') && postResults.length > 0 && (
                        <div className="space-y-2 pt-2">
                            <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1">Posts</h4>
                            {postResults.map(post => (
                                <div
                                    key={post.id}
                                    onClick={() => {
                                        navigate('post', { postId: post.id });
                                        onClose();
                                    }}
                                    className="p-3.5 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 cursor-pointer group transition-all space-y-1.5"
                                >
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="font-semibold text-slate-800 dark:text-slate-200">@{post.authorProfile?.username || 'user'}</span>
                                        {post.mood && (
                                            <span className="bg-brand-50 dark:bg-brand-500/15 text-brand-600 dark:text-brand-400 px-2 py-0.5 rounded-full text-[10px] font-medium border border-brand-200 dark:border-brand-500/20">
                                                {post.mood}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed">
                                        {post.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UniversalSearchModal;
